import { z } from 'zod';

/** Identifiants fermés des intentions UI que le modèle peut proposer. */
export const AGENT_UI_ACTION_IDS = [
  'bordereau.navigate_current_to_stamping',
  'bordereau.navigate_current_to_numbering_review',
  'bordereau.open_selection_for_stamping',
  'documents.preview',
  'case_analysis.open_section',
  'settings.open_section',
  'help.open_center',
] as const;

export type AgentUiActionId = (typeof AGENT_UI_ACTION_IDS)[number];

export const AgentUiActionIdSchema = z.enum(AGENT_UI_ACTION_IDS);

export const CASE_ANALYSIS_SECTIONS = [
  'apercu',
  'faits',
  'parties',
  'pieces',
  'procedure',
  'timeline',
  'nullites',
] as const;

export type CaseAnalysisSection = (typeof CASE_ANALYSIS_SECTIONS)[number];

export const CaseAnalysisSectionSchema = z.enum(CASE_ANALYSIS_SECTIONS);

export const SETTINGS_SECTIONS = ['memory', 'skills', 'bordereau', 'nomenclature'] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

export const SettingsSectionSchema = z.enum(SETTINGS_SECTIONS);

/** Un libellé plus long casse la mise en page compacte de la carte d'actions. */
export const AGENT_UI_ACTION_LABEL_MAX = 40;
export const AGENT_UI_ACTIONS_MAX = 3;
export const AGENT_UI_ACTION_PREVIEW_DOCUMENTS_MAX = 10;
export const AGENT_UI_ACTION_BORDEREAU_DOCUMENTS_MAX = 100;
/**
 * Borne de la sélection RÉSOLUE par le backend pour `selection: 'all_numbered'`.
 *
 * Égale à la limite de lecture des pièces d'un dossier (`BaseRepository.findAll`,
 * 1000 lignes) : le navigateur relit « Nos pièces » par la même requête avant
 * d'ouvrir le bordereau, une sélection plus longue n'y serait pas retrouvée.
 */
export const AGENT_UI_ACTION_BORDEREAU_RESOLVED_DOCUMENTS_MAX = 1000;

/**
 * Sélections que le backend résout lui-même pour le tamponnage.
 *
 * `all_numbered` = toutes les pièces numérotées de « Nos pièces » du dossier actif,
 * dans l'ordre naturel des numéros. Elle existe parce que recopier des dizaines
 * d'identifiants est une tâche où le modèle se trompe sans le voir : le 2026-10-26
 * (dossier MAITRE c/ HEXCEL, 56 pièces), il en a recopié 10 et a annoncé à l'avocate
 * que les 56 partaient au tamponnage.
 */
export const BORDEREAU_STAMPING_SELECTIONS = ['all_numbered'] as const;
export type BordereauStampingSelection = (typeof BORDEREAU_STAMPING_SELECTIONS)[number];

const labelSchema = z.string().trim().min(1).max(AGENT_UI_ACTION_LABEL_MAX);
const caseIdSchema = z.string().uuid();

const documentIdsSchema = (max: number) =>
  z
    .array(z.string().uuid())
    .min(1)
    // Les handlers doivent recevoir une sélection stable : le premier ordre du modèle fait foi.
    .transform((ids) => [...new Set(ids)])
    .pipe(z.array(z.string().uuid()).max(max));

const navigateCurrentToStampingProposalSchema = z
  .object({
    id: z.literal('bordereau.navigate_current_to_stamping'),
    label: labelSchema,
  })
  .strict();

const navigateCurrentToNumberingReviewProposalSchema = z
  .object({
    id: z.literal('bordereau.navigate_current_to_numbering_review'),
    label: labelSchema,
  })
  .strict();

/**
 * Exactement un de `documentIds` (sous-ensemble choisi par l'avocat) ou `selection`
 * (résolue par le backend). La règle « exactement un » vit dans le `superRefine`
 * de la frame : une branche d'union discriminée doit rester un ZodObject nu.
 */
const openSelectionForStampingProposalSchema = z
  .object({
    id: z.literal('bordereau.open_selection_for_stamping'),
    label: labelSchema,
    documentIds: documentIdsSchema(AGENT_UI_ACTION_BORDEREAU_DOCUMENTS_MAX).optional(),
    selection: z.enum(BORDEREAU_STAMPING_SELECTIONS).optional(),
  })
  .strict();

const previewDocumentsProposalSchema = z
  .object({
    id: z.literal('documents.preview'),
    label: labelSchema,
    documentIds: documentIdsSchema(AGENT_UI_ACTION_PREVIEW_DOCUMENTS_MAX),
  })
  .strict();

const openCaseAnalysisSectionProposalSchema = z
  .object({
    id: z.literal('case_analysis.open_section'),
    label: labelSchema,
    section: CaseAnalysisSectionSchema,
  })
  .strict();

const openSettingsSectionProposalSchema = z
  .object({
    id: z.literal('settings.open_section'),
    label: labelSchema,
    section: SettingsSectionSchema,
  })
  .strict();

const openHelpCenterProposalSchema = z
  .object({
    id: z.literal('help.open_center'),
    label: labelSchema,
  })
  .strict();

/**
 * Entrée non fiable produite par le modèle.
 *
 * Chaque branche est stricte pour que le modèle ne puisse choisir ni URL, ni
 * tenant. Le dossier se nomme une fois, par `case_id` sur la demande entière
 * (`AgentUiActionsPayloadSchema`), et la garde de portée le borne à la liste de
 * la conversation ; le backend injecte le dossier décidé dans chaque action.
 */
export const AgentUiActionProposalSchema = z.discriminatedUnion('id', [
  navigateCurrentToStampingProposalSchema,
  navigateCurrentToNumberingReviewProposalSchema,
  openSelectionForStampingProposalSchema,
  previewDocumentsProposalSchema,
  openCaseAnalysisSectionProposalSchema,
  openSettingsSectionProposalSchema,
  openHelpCenterProposalSchema,
]);

export type AgentUiActionProposal = z.infer<typeof AgentUiActionProposalSchema>;

const navigateCurrentToStampingResolvedSchema = navigateCurrentToStampingProposalSchema.extend({
  caseId: caseIdSchema,
});

const navigateCurrentToNumberingReviewResolvedSchema =
  navigateCurrentToNumberingReviewProposalSchema.extend({ caseId: caseIdSchema });

/**
 * La frame résolue porte toujours la liste explicite : le navigateur n'a pas à
 * connaître `selection`, le backend l'a déjà traduite en identifiants.
 */
const openSelectionForStampingResolvedSchema = z
  .object({
    id: z.literal('bordereau.open_selection_for_stamping'),
    label: labelSchema,
    documentIds: documentIdsSchema(AGENT_UI_ACTION_BORDEREAU_RESOLVED_DOCUMENTS_MAX),
    caseId: caseIdSchema,
  })
  .strict();

const previewDocumentsResolvedSchema = previewDocumentsProposalSchema.extend({
  caseId: caseIdSchema,
});

const openCaseAnalysisSectionResolvedSchema = openCaseAnalysisSectionProposalSchema.extend({
  caseId: caseIdSchema,
});

/** Frame backend vers frontend, enrichie avec le contexte fiable du tour. */
export const ResolvedAgentUiActionSchema = z.discriminatedUnion('id', [
  navigateCurrentToStampingResolvedSchema,
  navigateCurrentToNumberingReviewResolvedSchema,
  openSelectionForStampingResolvedSchema,
  previewDocumentsResolvedSchema,
  openCaseAnalysisSectionResolvedSchema,
  openSettingsSectionProposalSchema,
  openHelpCenterProposalSchema,
]);

export type ResolvedAgentUiAction = z.infer<typeof ResolvedAgentUiActionSchema>;

const rejectDuplicateActionIds = (
  actions: readonly { id: AgentUiActionId }[],
  context: z.RefinementCtx,
): void => {
  const seen = new Set<AgentUiActionId>();
  actions.forEach(({ id }, index) => {
    if (seen.has(id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [index, 'id'],
        message: `Agent UI action id must be unique within one frame; duplicate id: ${id}`,
      });
    }
    seen.add(id);
  });
};

const requireOneStampingSelection = (
  actions: readonly AgentUiActionProposal[],
  context: z.RefinementCtx,
): void => {
  actions.forEach((action, index) => {
    if (action.id !== 'bordereau.open_selection_for_stamping') return;
    const hasIds = action.documentIds !== undefined;
    const hasSelection = action.selection !== undefined;
    if (hasIds === hasSelection) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [index],
        message: hasIds
          ? 'bordereau.open_selection_for_stamping: give either documentIds or selection, not both. Use selection "all_numbered" for every numbered piece of the case, documentIds only for a subset the lawyer chose.'
          : 'bordereau.open_selection_for_stamping: missing selection. Use selection "all_numbered" for every numbered piece of the case, or documentIds for a subset the lawyer chose.',
      });
    }
  });
};

export const AgentUiActionsPayloadSchema = z
  .object({
    actions: z.array(AgentUiActionProposalSchema).min(1).max(AGENT_UI_ACTIONS_MAX),
    /** Dossier visé, validé par la garde de portée avant l'exécuteur ; absent = le principal. */
    case_id: z.string().optional(),
  })
  .strict()
  .superRefine(({ actions }, context) => {
    rejectDuplicateActionIds(actions, context);
    requireOneStampingSelection(actions, context);
  });

export type AgentUiActionsPayload = z.infer<typeof AgentUiActionsPayloadSchema>;

export const ResolvedAgentUiActionsPayloadSchema = z
  .object({
    actions: z.array(ResolvedAgentUiActionSchema).min(1).max(AGENT_UI_ACTIONS_MAX),
  })
  .strict()
  .superRefine(({ actions }, context) => rejectDuplicateActionIds(actions, context));

export type ResolvedAgentUiActionsPayload = z.infer<typeof ResolvedAgentUiActionsPayloadSchema>;

const agentUiActionIds = new Set<string>(AGENT_UI_ACTION_IDS);

function isAgentUiActionId(id: string): id is AgentUiActionId {
  return agentUiActionIds.has(id);
}

/**
 * Capabilities déclarées par l'hôte pour un tour, normalisées sans réordonnancement.
 * Les ids futurs sont ignorés à cette frontière de rollout ; ils ne deviennent
 * jamais exécutables tant qu'ils ne figurent pas dans le catalogue fermé.
 */
export const AgentUiActionCapabilitiesSchema = z
  .array(z.string())
  .transform((ids): AgentUiActionId[] => [...new Set(ids.filter(isAgentUiActionId))]);

export type AgentUiActionCapabilities = z.infer<typeof AgentUiActionCapabilitiesSchema>;

export type AgentUiActionScope = 'current_host' | 'global_surface';

export interface AgentUiActionDefinition {
  readonly id: AgentUiActionId;
  readonly scope: AgentUiActionScope;
  readonly modelWhenToUse: string;
  readonly userEffect: string;
  readonly requiresCase: boolean;
  readonly mutatesBusinessData: boolean;
}

/**
 * Catalogue purement descriptif partagé avec le builder du tool.
 * Les handlers et contrôles d'autorisation restent dans leurs applications.
 */
export const AGENT_UI_ACTION_CATALOG = [
  {
    id: 'bordereau.navigate_current_to_stamping',
    scope: 'current_host',
    modelWhenToUse: 'Le chat est déjà dans le bordereau et la numérotation a été appliquée.',
    userEffect: 'Prépare le bordereau courant puis ouvre le tamponnage.',
    requiresCase: true,
    mutatesBusinessData: true,
  },
  {
    id: 'bordereau.navigate_current_to_numbering_review',
    scope: 'current_host',
    modelWhenToUse: 'Le chat est déjà dans le bordereau et l’utilisateur veut relire la numérotation.',
    userEffect: 'Affiche la relecture de la numérotation du bordereau courant.',
    requiresCase: true,
    mutatesBusinessData: false,
  },
  {
    id: 'bordereau.open_selection_for_stamping',
    scope: 'global_surface',
    modelWhenToUse: 'Une sélection de pièces numérotées doit être ouverte à l’étape 1 du bordereau pour relecture. Pour toutes les pièces numérotées du dossier, passe selection "all_numbered" et ne recopie aucun identifiant ; documentIds ne sert qu’à un sous-ensemble choisi par l’avocat.',
    userEffect: 'Ouvre les pièces sélectionnées à l’étape 1 du bordereau pour relecture ; le tamponnage ne sera préparé qu’après un clic explicite sur « Continuer ».',
    requiresCase: true,
    mutatesBusinessData: false,
  },
  {
    id: 'documents.preview',
    scope: 'global_surface',
    modelWhenToUse: 'Une ou plusieurs pièces identifiées doivent être montrées à l’utilisateur.',
    userEffect: 'Ouvre les pièces demandées dans le visualiseur.',
    requiresCase: true,
    mutatesBusinessData: false,
  },
  {
    id: 'case_analysis.open_section',
    scope: 'global_surface',
    modelWhenToUse: 'L’utilisateur veut consulter une section précise de l’analyse du dossier.',
    userEffect: 'Ouvre la section demandée dans l’analyse du dossier.',
    requiresCase: true,
    mutatesBusinessData: false,
  },
  {
    id: 'settings.open_section',
    scope: 'global_surface',
    modelWhenToUse: 'L’utilisateur veut consulter une section précise de ses réglages.',
    userEffect: 'Ouvre la section demandée dans les réglages.',
    requiresCase: false,
    mutatesBusinessData: false,
  },
  {
    id: 'help.open_center',
    scope: 'global_surface',
    modelWhenToUse: 'L’utilisateur demande de l’aide sur le produit.',
    userEffect: 'Ouvre le centre d’aide Hector.',
    requiresCase: false,
    mutatesBusinessData: false,
  },
] as const satisfies readonly AgentUiActionDefinition[];

/** @deprecated Use `AgentUiActionProposalSchema`. */
export const AgentUiActionSchema = AgentUiActionProposalSchema;
/** @deprecated Use `AgentUiActionProposal`. */
export type AgentUiAction = AgentUiActionProposal;
